'use client'

import { motion } from 'framer-motion'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { ChevronDownIcon, CircleHelpIcon, EllipsisVerticalIcon, UnplugIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import React, {
  ComponentPropsWithoutRef,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

import { MoreDotsIcon } from '@/components/shell-icons'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { WIDGET_MENU_ROW_CLASS } from '@/components/widget/widget-menu-row'
import {
  WidgetSettingsPopover,
  type WidgetSettingsSection,
} from '@/components/widget/widget-settings-popover'
import { useAuthGuard } from '@/features/auth/hooks/use-auth-guard'
import { useWidgetConfigSync } from '@/features/dashboard/hooks/use-widget-config-sync'
import { LinkingPageContext } from '@/features/dashboard/lib/linking-page-context'
import {
  useMobileLayout,
  useMobileWidgetMenuSlot,
} from '@/features/dashboard/lib/mobile-layout-context'
import { ChatContainerContext } from '@/features/order/chat/lib/chat-container-context'
import { useOpenWidgetTutorial } from '@/features/tutorial/hooks/use-open-widget-tutorial'
import { useLinkingColorMigration } from '@/hooks/use-linking-group-state'
import { cn } from '@/lib/utils'
import { fullscreenGroupIdAtom } from '@/state/layout'
import {
  colorGlowTriggersAtom,
  colorTickerGlowTriggersAtom,
  glowTriggersAtom,
  LinkingColor,
  linkingColorsAtomFamily,
  triggerColorGlow,
  triggerColorTickerGlow,
} from '@/state/linking'
import { widgetActionAtom } from '@/state/widget-actions'

// ========== Types & Config ==========
interface LinkingColorOption {
  id: LinkingColor
  /** Translated color name for accessible labels and tooltips. */
  labelKey: LinkingColorLabelKey
  /** Swatch fill (a CSS color value): linking-group colors are data, not theme styling. */
  swatch: string
  glowColor: string
  number?: number
}

type LinkingColorLabelKey =
  | 'unlinked'
  | 'linkColorMint'
  | 'linkColorYellow'
  | 'linkColorOrange'
  | 'linkColorPink'
  | 'linkColorPurple'
  | 'linkColorBlue'
  | 'linkColorGreen'

// Neutral grey swatch for "unlinked", theme-relative (≈ foreground/20).
const UNLINKED_SWATCH = 'color-mix(in srgb, var(--foreground) 20%, transparent)'

export const LINKING_COLORS: LinkingColorOption[] = [
  {
    id: 'mint',
    labelKey: 'linkColorMint',
    // Figma link/mint.
    swatch: '#22D3EE',
    glowColor: 'rgba(34, 211, 238, 0.6)',
  },
  {
    id: 'yellow',
    labelKey: 'linkColorYellow',
    swatch: '#FBFF10',
    glowColor: 'rgba(250, 204, 21, 0.6)',
  },
  {
    id: 'orange',
    labelKey: 'linkColorOrange',
    swatch: '#F26B22',
    glowColor: 'rgba(249, 115, 22, 0.6)',
  },
  {
    id: 'pink',
    labelKey: 'linkColorPink',
    swatch: '#FF57CD',
    glowColor: 'rgba(255, 87, 205, 0.6)',
  },
  {
    id: 'purple',
    labelKey: 'linkColorPurple',
    swatch: '#8855FF',
    glowColor: 'rgba(168, 85, 247, 0.6)',
  },
  {
    id: 'blue',
    labelKey: 'linkColorBlue',
    swatch: '#1457FF',
    glowColor: 'rgba(20, 87, 255, 0.6)',
  },
  {
    id: 'green',
    labelKey: 'linkColorGreen',
    swatch: '#17DD5C',
    glowColor: 'rgba(34, 197, 94, 0.6)',
  },
]

// SVG, not a sized div: the trade page renders under `zoom: 0.9`, where a 12px box
// becomes 10.8px and the browser snaps its width and height to whole pixels
// independently, so a div swatch can come out a pixel wider than tall. The viewBox
// keeps the square's aspect ratio inside whatever box it snaps to.
export function LinkingSwatch({ color, className }: { color: string; className?: string }) {
  return (
    <svg viewBox="0 0 12 12" aria-hidden className={cn('block size-3 shrink-0', className)}>
      <rect width="12" height="12" rx="4" style={{ fill: color }} />
    </svg>
  )
}

// Figma "Widget header" → Link: a 36px slot centring the 12px dot, with no divider after it.
// Sized in design pixels (--wpx) so it lands on 36/12 on screen under the trade page's zoom.
const LINK_SLOT_CLASS =
  'flex h-9 w-[calc(36*var(--wpx))] shrink-0 items-center justify-center overflow-hidden'
const LINK_DOT_CLASS = 'size-[calc(12*var(--wpx))]'

/** The one mark for "unlinked" (a card or tab group with no link color) everywhere it shows. */
export function UnlinkedIcon(props: React.ComponentProps<typeof UnplugIcon>) {
  return <UnplugIcon {...props} />
}

export function getLinkingColorOption(color: LinkingColor | 'unlinked'): LinkingColorOption {
  if (color === 'unlinked') {
    return {
      id: 'unlinked' as LinkingColor,
      labelKey: 'unlinked',
      swatch: UNLINKED_SWATCH,
      glowColor: 'rgba(255, 255, 255, 0.3)', // Use a slightly brighter grey/white for the unlinked glow to make it visible
    }
  }
  return LINKING_COLORS.find((c) => c.id === color) || LINKING_COLORS[0]
}

// ========== Color Selector Component ==========
export interface LinkingColorSelectorProps {
  value?: LinkingColor
  onChange?: (color: LinkingColor) => void
  disabled?: boolean
  isColorGlowing?: boolean
  isTriggerGlowing?: boolean
  onTriggerGlowComplete?: () => void
  onColorGlowComplete?: () => void
  className?: string
  /** False drops the Unlinked choice — for a card that must always sit in a group. */
  allowUnlinked?: boolean
}

export function LinkingColorSelector({
  value,
  onChange,
  disabled,
  isColorGlowing,
  isTriggerGlowing,
  onTriggerGlowComplete,
  onColorGlowComplete,
  className,
  allowUnlinked = true,
}: LinkingColorSelectorProps) {
  const t = useTranslations('trade')
  const [open, setOpen] = useState(false)
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const currentColor = value ?? 'green'
  const selectedColor = getLinkingColorOption(currentColor)
  const groupName = t(selectedColor.labelKey)
  const chatContainer = useContext(ChatContainerContext)
  const portalContainer = chatContainer?.ownerDocument.body
  const hasMountedRef = useRef(false)
  useEffect(() => {
    hasMountedRef.current = true
  }, [])

  const handleSelect = (color: LinkingColor) => {
    onChange?.(color)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip open={tooltipOpen && !open} onOpenChange={setTooltipOpen}>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild disabled={disabled}>
            <motion.button
              data-tour="link-dot"
              className={cn(
                LINK_SLOT_CLASS,
                'no-drag not-disabled:hover:bg-foreground/5 data-[state=open]:bg-foreground/5 cursor-pointer gap-2 transition-colors duration-150',
                disabled && 'cursor-not-allowed opacity-50',
                className
              )}
              animate={
                isTriggerGlowing
                  ? {
                      boxShadow: [
                        `inset 0 0 0px ${selectedColor.glowColor}`,
                        `inset 0 0 20px ${selectedColor.glowColor}, inset 0 0 40px ${selectedColor.glowColor}`,
                        `inset 0 0 15px ${selectedColor.glowColor}, inset 0 0 30px ${selectedColor.glowColor}`,
                        `inset 0 0 0px ${selectedColor.glowColor}`,
                      ],
                    }
                  : {
                      boxShadow: `inset 0 0 0px ${selectedColor.glowColor}`,
                    }
              }
              transition={
                isTriggerGlowing
                  ? {
                      duration: 1.2,
                      repeat: 0,
                      ease: 'easeInOut',
                      times: [0, 0.3, 0.7, 1],
                    }
                  : { duration: 0 }
              }
              onAnimationComplete={() => {
                if (isTriggerGlowing) onTriggerGlowComplete?.()
              }}
              aria-label={
                currentColor === 'unlinked'
                  ? t('unlinked')
                  : t('linkGroupLabel', { name: groupName })
              }
            >
              <motion.div
                className={cn(LINK_DOT_CLASS, 'shrink-0 rounded-sm')}
                animate={
                  isColorGlowing
                    ? {
                        boxShadow: [
                          `0 0 0px ${selectedColor.glowColor}`,
                          `0 0 8px ${selectedColor.glowColor}, 0 0 12px ${selectedColor.glowColor}`,
                          `0 0 6px ${selectedColor.glowColor}, 0 0 10px ${selectedColor.glowColor}`,
                          `0 0 0px ${selectedColor.glowColor}`,
                        ],
                        scale: [1, 1.15, 1.1, 1],
                      }
                    : {
                        boxShadow: `0 0 0px ${selectedColor.glowColor}`,
                        scale: 1,
                      }
                }
                transition={
                  isColorGlowing
                    ? {
                        duration: 1.2,
                        repeat: 0,
                        ease: 'easeInOut',
                        times: [0, 0.3, 0.7, 1],
                      }
                    : { duration: 0 }
                }
                onAnimationComplete={() => {
                  if (isColorGlowing) onColorGlowComplete?.()
                }}
              >
                {/* A new colour pops in with a twist (prototype link dot); the first paint
                    doesn't, so a page full of widgets doesn't all pop on load. */}
                {currentColor === 'unlinked' ? (
                  <UnlinkedIcon className="size-full" aria-hidden />
                ) : (
                  <motion.span
                    key={currentColor}
                    className="block size-full"
                    initial={hasMountedRef.current ? { scale: 0.4, rotate: -45 } : false}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: 'spring', stiffness: 600, damping: 18 }}
                  >
                    <LinkingSwatch color={selectedColor.swatch} className="size-full" />
                  </motion.span>
                )}
              </motion.div>
            </motion.button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="start" className="text-xs">
          {currentColor === 'unlinked' ? t('unlinked') : t('linkGroupLabel', { name: groupName })}
        </TooltipContent>
      </Tooltip>
      <PopoverContent className="no-drag w-auto p-1" align="start" container={portalContainer}>
        <div className="grid grid-cols-4 gap-1">
          {(allowUnlinked
            ? [getLinkingColorOption('unlinked'), ...LINKING_COLORS]
            : LINKING_COLORS
          ).map((color) => (
            <Button
              key={color.id}
              type="button"
              variant={currentColor === color.id ? 'secondary' : 'ghost'}
              size="iconXs"
              shape="square"
              aria-label={t(color.labelKey)}
              title={t(color.labelKey)}
              aria-pressed={currentColor === color.id}
              onClick={() => handleSelect(color.id)}
            >
              {color.id === 'unlinked' ? (
                <UnlinkedIcon className="size-3" aria-hidden />
              ) : (
                <LinkingSwatch color={color.swatch} />
              )}
            </Button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}

// Static title for widgets that do not participate in linking groups.
export function WidgetHeaderLabel({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <span className={cn('block min-w-0 truncate px-3 text-xs font-medium', className)}>
      {children}
    </span>
  )
}

// ========== Widget Actions Menu ==========
/**
 * The scrollable body of the "..." menu (everything under its title). Shows a fade and
 * a chevron at the bottom edge while more rows sit below the fold, so a capped menu
 * reads as scrollable rather than as complete.
 */
function MenuScrollRegion({ children }: { children: React.ReactNode }) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const [hasMoreBelow, setHasMoreBelow] = useState(false)

  useEffect(() => {
    const scroller = scrollRef.current
    const content = contentRef.current
    if (!scroller || !content) return
    const update = () =>
      setHasMoreBelow(scroller.scrollTop + scroller.clientHeight < scroller.scrollHeight - 1)
    update()
    scroller.addEventListener('scroll', update, { passive: true })
    // Inline accordions (FilterRow `inline`) grow/shrink the content without a scroll.
    const observer = new ResizeObserver(update)
    observer.observe(scroller)
    observer.observe(content)
    return () => {
      scroller.removeEventListener('scroll', update)
      observer.disconnect()
    }
  }, [])

  const scrollDown = () =>
    scrollRef.current?.scrollBy({ top: scrollRef.current.clientHeight * 0.8, behavior: 'smooth' })

  return (
    <div className="relative flex min-h-0 flex-col">
      <div ref={scrollRef} className="min-h-0 overflow-y-auto">
        <div ref={contentRef} className="flex flex-col">
          {children}
        </div>
      </div>
      {hasMoreBelow && (
        <button
          type="button"
          tabIndex={-1}
          aria-hidden
          onClick={scrollDown}
          className="from-popover absolute inset-x-0 bottom-0 flex h-7 cursor-pointer items-end justify-center bg-linear-to-t to-transparent pb-0.5"
        >
          <ChevronDownIcon className="text-foreground/50 size-3.5" />
        </button>
      )}
    </div>
  )
}

interface WidgetActionsMenuProps {
  cardId: string
  className?: string
  // Render prop (not a plain node) so each item can decide for itself whether
  // opening it should close this outer menu — a settings/filter panel should
  // stay open through multi-step interaction, a one-shot action should close
  // on click. Passing `closeMenu` explicitly avoids the previous behavior of
  // closing on ANY click bubbling through additionalItems (see removed
  // wrapper below), which broke nested interactive content.
  additionalItems?: (closeMenu: () => void) => React.ReactNode
  // Rendered as its own popover between the fullscreen toggle and the "..." trigger —
  // see WidgetSettingsPopover's doc comment for why this is separate from
  // `additionalItems` (structural actions vs. widget configuration).
  settingsSections?: WidgetSettingsSection[]
  hideLayoutActions?: boolean
}

export function WidgetActionsMenu({
  cardId,
  className,
  additionalItems,
  settingsSections,
  hideLayoutActions = false,
}: WidgetActionsMenuProps) {
  const t = useTranslations('trade')
  const isMobile = useMobileLayout()
  const [open, setOpen] = useState(false)
  const setWidgetAction = useSetAtom(widgetActionAtom)
  const { requireAuth } = useAuthGuard()
  const [fullscreenId, setFullscreenId] = useAtom(fullscreenGroupIdAtom)
  const isFullscreen = fullscreenId === cardId
  const openTutorial = useOpenWidgetTutorial(cardId)

  // In the mobile stack this menu moves up into the stack row's header (the slot) and
  // merges with the row's own actions; the row owns layout (collapse/remove), so the grid
  // layout actions drop out.
  const mobileSlot = useMobileWidgetMenuSlot()
  const slot = isMobile ? mobileSlot : null
  const claimSlot = slot?.claim
  useEffect(() => claimSlot?.(), [claimSlot])
  const showLayoutActions = !hideLayoutActions && !isMobile
  const hasMenu = !!additionalItems || !!openTutorial || showLayoutActions || !!slot

  const handleToggleFullscreen = () => {
    setFullscreenId(isFullscreen ? null : cardId)
    setOpen(false)
  }

  const handleClose = () => {
    requireAuth(() => {
      setWidgetAction({ type: 'close', cardId })
      setOpen(false)
    })
  }

  const handleDuplicate = () => {
    requireAuth(() => {
      setWidgetAction({ type: 'duplicate', cardId })
      setOpen(false)
    })
  }

  const handleMoveToTop = () => {
    requireAuth(() => {
      setWidgetAction({ type: 'move-to-top', cardId })
      setOpen(false)
    })
  }

  const handleMinimize = () => {
    requireAuth(() => {
      setWidgetAction({ type: 'minimize', cardId })
      setOpen(false)
    })
  }

  const menu = (
    // Figma "Widget header" → Actions: 24px ghost icon buttons, 6px in from the right edge.
    <div className={cn('flex items-center', !slot && 'pr-[calc(6*var(--wpx))]')}>
      {settingsSections && <WidgetSettingsPopover sections={settingsSections} />}
      {hasMenu && (
        <DropdownMenu open={open} onOpenChange={setOpen}>
          <DropdownMenuTrigger asChild>
            <motion.button
              className={cn([
                slot
                  ? // Match the stack header's own icon buttons.
                    'no-drag not-disabled:hover:bg-foreground/10 data-[state=open]:bg-foreground/10 flex size-9 cursor-pointer items-center justify-center'
                  : 'no-drag text-foreground/60 not-disabled:hover:bg-foreground/5 not-disabled:hover:text-foreground data-[state=open]:bg-foreground/5 data-[state=open]:text-foreground flex size-[calc(24*var(--wpx))] cursor-pointer items-center justify-center rounded-sm transition-colors duration-150',
                className,
              ])}
              whileTap={{ scale: slot ? 0.95 : 0.9 }}
              aria-label={t('widgetActions')}
              // Tutorial steps about a setting in this menu point here (TutorialStep.inMenu).
              data-widget-menu-trigger=""
            >
              {slot ? (
                <EllipsisVerticalIcon className="text-foreground/40 size-4" />
              ) : (
                <MoreDotsIcon className="h-[calc(11.5*var(--wpx))] w-[calc(2.5*var(--wpx))]" />
              )}
            </motion.button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            // Capped so long menus (positions' column/filter rows) don't run to the
            // viewport edge. Menu items scroll as one list
            // (MenuScrollRegion), with a hint at the bottom edge while more rows are hidden.
            className={cn(
              'no-drag flex max-h-[min(var(--radix-dropdown-menu-content-available-height),360px)] flex-col overflow-y-hidden',
              slot ? 'w-48' : 'w-36'
            )}
            align="end"
            onCloseAutoFocus={(e) => e.preventDefault()}
          >
            <MenuScrollRegion>
              {additionalItems?.(() => setOpen(false))}
              {additionalItems && (openTutorial || showLayoutActions) && (
                <Separator className="bg-foreground/10 my-1" />
              )}
              {openTutorial && (
                <motion.button
                  onClick={() => {
                    setOpen(false)
                    openTutorial()
                  }}
                  className={WIDGET_MENU_ROW_CLASS}
                  whileTap={{ scale: 0.98 }}
                  transition={{ duration: 0.15 }}
                >
                  <span className="flex-1">{t('tutorial')}</span>
                  <CircleHelpIcon className="text-foreground/50 size-3.5" aria-hidden />
                </motion.button>
              )}
              {showLayoutActions && (
                <>
                  <motion.button
                    onClick={handleToggleFullscreen}
                    className={WIDGET_MENU_ROW_CLASS}
                    whileTap={{ scale: 0.98 }}
                    transition={{ duration: 0.15 }}
                  >
                    <span>{t(isFullscreen ? 'exitFullscreen' : 'enterFullscreen')}</span>
                  </motion.button>
                  <motion.button
                    onClick={handleMoveToTop}
                    className={WIDGET_MENU_ROW_CLASS}
                    whileTap={{ scale: 0.98 }}
                    transition={{ duration: 0.15 }}
                  >
                    <span>{t('moveToTop')}</span>
                  </motion.button>
                  <motion.button
                    onClick={handleMinimize}
                    className={WIDGET_MENU_ROW_CLASS}
                    whileTap={{ scale: 0.98 }}
                    transition={{ duration: 0.15 }}
                  >
                    <span>{t('minimize')}</span>
                  </motion.button>
                  <motion.button
                    onClick={handleDuplicate}
                    className={WIDGET_MENU_ROW_CLASS}
                    whileTap={{ scale: 0.98 }}
                    transition={{ duration: 0.15 }}
                  >
                    <span>{t('duplicate')}</span>
                  </motion.button>
                  <motion.button
                    onClick={handleClose}
                    className={WIDGET_MENU_ROW_CLASS}
                    whileTap={{ scale: 0.98 }}
                    transition={{ duration: 0.15 }}
                  >
                    <span className="text-chart-red">{t('deleteWidget')}</span>
                  </motion.button>
                </>
              )}
              {slot && (
                <>
                  {(additionalItems || openTutorial) && (
                    <Separator className="bg-foreground/10 my-1" />
                  )}
                  {slot.extraItems(() => setOpen(false))}
                </>
              )}
            </MenuScrollRegion>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )

  if (slot) return slot.element ? createPortal(menu, slot.element) : null
  return menu
}

// ========== Main Component ==========
interface Props extends ComponentPropsWithoutRef<'div'> {
  cardId?: string // Optional: Unique ID for this card (auto-generated if not provided)
  disableLinking?: boolean
  disableActionsMenu?: boolean
  /**
   * Hides only the grid-specific actions (fullscreen toggle/move-to-top/duplicate/
   * delete) instead of the whole "..." menu — for a card with no matching grid item
   * (those actions would be meaningless) that still wants its `additionalMenuItems`
   * (e.g. Columns/Settings) reachable. Ignored when `disableActionsMenu` is set.
   */
  hideLayoutActions?: boolean
  additionalMenuItems?: (closeMenu: () => void) => React.ReactNode
  settingsSections?: WidgetSettingsSection[]
  /** Called when the user picks a different link color for this card. */
  onLinkingColorChange?: (newColor: LinkingColor, prevColor: LinkingColor) => void
  /** False drops the Unlinked choice; a card already saved as unlinked shows as mint. */
  allowUnlinked?: boolean
}

export function LinkingActionHeader({
  cardId: externalCardId,
  children,
  className,
  disableLinking = false,
  disableActionsMenu = false,
  hideLayoutActions = false,
  additionalMenuItems,
  settingsSections,
  onLinkingColorChange,
  allowUnlinked = true,
  ...props
}: Props) {
  const autoCardId = useId()
  const cardId = externalCardId ?? autoCardId
  const pageId = useContext(LinkingPageContext)

  const [linkingColors, setLinkingColors] = useAtom(linkingColorsAtomFamily(pageId))
  const savedColor = linkingColors[cardId] ?? 'mint'
  const linkingColor = !allowUnlinked && savedColor === 'unlinked' ? 'mint' : savedColor

  const [glowTriggers] = useAtom(glowTriggersAtom)

  const [colorGlowTriggers] = useAtom(colorGlowTriggersAtom)
  const setColorGlowTriggers = useSetAtom(colorGlowTriggersAtom)

  const [colorTickerGlowTriggers] = useAtom(colorTickerGlowTriggersAtom)
  const setColorTickerGlowTriggers = useSetAtom(colorTickerGlowTriggersAtom)

  // Migration hook for handling state when switching to unlinked
  const { cleanupPreviousUnlinkedOptionRows, migrateToUnlinked } = useLinkingColorMigration(cardId)
  const { syncConfig } = useWidgetConfigSync()

  const [isBorderGlowing, setIsBorderGlowing] = useState(false)
  const [isColorGlowing, setIsColorGlowing] = useState(false)

  const [localColorGlowTrigger, setLocalColorGlowTrigger] = useState(0)
  const [localBorderGlowTrigger, setLocalBorderGlowTrigger] = useState(0)

  // Track previous counts to prevent duplicate triggers from re-renders.
  // Initialize with current atom values so that on remount (e.g. key change),
  // already-accumulated counts don't cause a spurious glow on mount.
  const prevBorderCountRef = useRef(colorTickerGlowTriggers[linkingColor] ?? 0)
  const prevColorCountRef = useRef(colorGlowTriggers[linkingColor] ?? 0)
  const prevCardGlowCountRef = useRef(glowTriggers[cardId]?.count ?? 0)

  // Handle color change with state migration
  const handleColorChange = (newColor: LinkingColor) => {
    if (newColor !== linkingColor) onLinkingColorChange?.(newColor, linkingColor)
    // If switching from linked to unlinked, copy current group state to card's isolated state
    if (linkingColor !== 'unlinked' && newColor === 'unlinked') {
      migrateToUnlinked()
    }
    if (linkingColor === 'unlinked' && newColor !== 'unlinked') {
      cleanupPreviousUnlinkedOptionRows()
    }

    setLinkingColors((prev) => ({ ...prev, [cardId]: newColor }))
    syncConfig(cardId, { group: newColor })

    // Trigger color-change glow for the specific color group or local card if unlinked
    if (newColor === 'unlinked') {
      setLocalColorGlowTrigger((prev) => prev + 1)
    } else {
      triggerColorGlow(setColorGlowTriggers, newColor)
    }
  }
  // Register this card with default color if not already registered
  // Use a ref to ensure we only register once, avoiding re-runs whenever
  // linkingColors changes (which would race with glow triggers on first click)
  const registeredRef = useRef(false)
  useEffect(() => {
    if (!registeredRef.current) {
      registeredRef.current = true
      setLinkingColors((prev) => {
        if (cardId in prev) return prev
        return { ...prev, [cardId]: 'mint' }
      })
    }
  }, [cardId, setLinkingColors])

  // Watch for glow trigger changes for this card and its color group
  useEffect(() => {
    const trigger = glowTriggers[cardId]
    const currentCount = trigger?.count ?? 0

    // Only trigger if count actually increased
    if (
      trigger &&
      currentCount > prevCardGlowCountRef.current &&
      trigger.type === 'ticker-change'
    ) {
      prevCardGlowCountRef.current = currentCount
      if (linkingColor === 'unlinked') {
        setLocalBorderGlowTrigger((prev) => prev + 1)
      } else {
        triggerColorTickerGlow(setColorTickerGlowTriggers, linkingColor)
      }
    }
  }, [glowTriggers[cardId]?.count, cardId, linkingColor, setColorTickerGlowTriggers])

  useEffect(() => {
    let count = 0
    if (linkingColor === 'unlinked') {
      count = localBorderGlowTrigger
    } else {
      count = colorTickerGlowTriggers[linkingColor] ?? 0
    }

    // Only trigger if count actually increased.
    // Reset is handled by onAnimationComplete in LinkingColorSelector (no setTimeout needed).
    if (count > prevBorderCountRef.current) {
      prevBorderCountRef.current = count
      setIsBorderGlowing(true)
    }
  }, [colorTickerGlowTriggers[linkingColor], linkingColor, localBorderGlowTrigger])

  useEffect(() => {
    let count = 0
    if (linkingColor === 'unlinked') {
      count = localColorGlowTrigger
    } else {
      count = colorGlowTriggers[linkingColor] ?? 0
    }

    // Only trigger if count actually increased.
    // Reset is handled by onAnimationComplete in LinkingColorSelector (no setTimeout needed).
    if (count > prevColorCountRef.current) {
      prevColorCountRef.current = count
      setIsColorGlowing(true)
    }
  }, [colorGlowTriggers[linkingColor], linkingColor, localColorGlowTrigger])

  return (
    <div className={cn('relative overflow-visible py-0 pl-0', className)} {...props}>
      <div className="relative flex flex-row items-center overflow-hidden">
        {/* Linking Color Selector */}
        <LinkingColorSelector
          value={linkingColor}
          onChange={handleColorChange}
          disabled={disableLinking}
          allowUnlinked={allowUnlinked}
          isColorGlowing={isColorGlowing}
          isTriggerGlowing={isBorderGlowing}
          onTriggerGlowComplete={() => setIsBorderGlowing(false)}
          onColorGlowComplete={() => setIsColorGlowing(false)}
        />

        {/* Content */}
        <div className="min-w-0 flex-1">{children}</div>

        {/* Widget actions (only when not disabled — use WidgetActionsMenu directly for custom placement) */}
        {!disableActionsMenu && (
          <WidgetActionsMenu
            cardId={cardId}
            additionalItems={additionalMenuItems}
            settingsSections={settingsSections}
            hideLayoutActions={hideLayoutActions}
          />
        )}
      </div>
    </div>
  )
}
